import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { JapaneseVehiclePlateInput } from '@/components/efs/JapaneseVehiclePlateInput';
import {
  composeJapaneseVehiclePlate,
  parseJapaneseVehiclePlate,
} from '@/components/efs/japanese-vehicle-plate';

import {
  dispatchRequestAcceptSchema,
  type DispatchRequestAcceptInput,
  type DispatchRequestDto,
} from '../types';
import { useAcceptRequest, useRecentDispatchVehicles } from '../useDispatch';

const NS = 'operations';

interface AcceptDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchRequestDto | null;
}

/**
 * Facility-side accept dialog. The relevant field depends on the request
 * type — `dispatch_out` collects `vehicle_number` (車牌/車番);
 * `delivery_in` collects `receivable_date` (可收貨日). Both endpoints
 * accept either field, so we render only the relevant one.
 */
export const AcceptDispatchDialog = ({
  open,
  onOpenChange,
  request,
}: AcceptDispatchDialogProps) => {
  const { t } = useTranslation(NS);
  const accept = useAcceptRequest();
  const [ccEmails, setCcEmails] = useState('');
  const isDispatchOut = request?.request_type === 'dispatch_out';

  const form = useForm<DispatchRequestAcceptInput>({
    resolver: zodResolver(dispatchRequestAcceptSchema),
    defaultValues: { vehicle_number: '', receivable_date: '' },
  });

  // `dispatch_out` requests never carry a plate (the supplier UI does not
  // collect one), so offer the trucks this facility used most recently.
  const recentVehicles = useRecentDispatchVehicles(request?.routed_facility_id, {
    enabled: open && isDispatchOut,
  });

  // BE normalizes too, but the same truck can still arrive spelled two ways
  // ('品川 500 あ 1234' / '品川500あ1234'); collapse them to one chip.
  const vehicleSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of recentVehicles.data ?? []) {
      const plate = composeJapaneseVehiclePlate(parseJapaneseVehiclePlate(row.vehicle_number));
      if (!plate || seen.has(plate)) continue;
      seen.add(plate);
      out.push(plate);
    }
    return out;
  }, [recentVehicles.data]);

  useEffect(() => {
    if (!open) return;
    setCcEmails('');
    form.reset({
      vehicle_number: request?.vehicle_number ?? '',
      receivable_date: request?.receivable_date ?? new Date().toISOString().slice(0, 10),
    });
  }, [open, request, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!request) return;
    const cc = ccEmails.split(',').map((email) => email.trim()).filter(Boolean);
    const body: DispatchRequestAcceptInput = isDispatchOut
      ? { vehicle_number: values.vehicle_number || undefined, ...(cc.length ? { cc_emails: cc } : {}) }
      : { receivable_date: values.receivable_date || undefined, ...(cc.length ? { cc_emails: cc } : {}) };
    await accept.mutateAsync({ id: request.dispatch_request_id, body });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.operations.dispatch.actions.accept')}</DialogTitle>
          {request ? (
            <DialogDescription>
              {t(`efs.operations.dispatch.types.${request.request_type}`, {
                defaultValue: request.request_type,
              })}
              {request.requested_date ? ` · ${request.requested_date}` : ''}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <label className="text-sm font-medium" htmlFor="accept-cc-emails">CC Email</label>
              <Input id="accept-cc-emails" value={ccEmails} onChange={(event) => setCcEmails(event.target.value)} placeholder="多筆請以逗號分隔" />
            </div>
            {isDispatchOut ? (
              <FormField
                control={form.control}
                name="vehicle_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.vehicle_number')}</FormLabel>
                    {vehicleSuggestions.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-efs-text-muted">
                          {t('efs.operations.dispatch.facility.recent_vehicles')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {vehicleSuggestions.map((plate) => (
                            <button
                              key={plate}
                              type="button"
                              className="max-w-full truncate rounded-md border border-efs-border bg-efs-bg-subtle px-2.5 py-1 text-xs text-efs-text-primary hover:bg-efs-bg-panel"
                              onClick={() => field.onChange(plate)}
                              title={plate}
                            >
                              {plate}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <FormControl>
                      <JapaneseVehiclePlateInput
                        id="dispatch-vehicle-number"
                        ref={field.ref}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="receivable_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.receivable_date')}</FormLabel>
                    <FormControl>
                      <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={accept.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={accept.isPending}>
                {accept.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.operations.dispatch.actions.accept')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptDispatchDialog;
